import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

/**
 * Fetches the best WhatsApp match for a lead card.
 *
 * Extracted from LeadsGridCard, which fetched inline. On a three-column
 * infinite-scroll grid that meant one request per card, none of them
 * cancellable, and a setState on every card unmounted mid-flight — routine
 * when scrolling quickly.
 *
 * Two changes:
 *   - AbortController, so a card that unmounts cancels rather than resolving
 *     into a dead component.
 *   - A module-level cache keyed product_id|message_id. Cards remount
 *     constantly (tab switches, filter changes, list re-renders) and the same
 *     pair is re-requested each time. In-flight promises are shared too, so N
 *     cards mounting on the same key produce one request, not N.
 *
 * Cache lifetime is the page session. Matches are derived server-side from
 * messages already sent, so they are effectively immutable for a session; a
 * reload clears it.
 *
 * Same-origin proxy on the Laravel backend — see useTradeImage.js for the
 * rationale (production CORS block on duckdns).
 */

const WHATSAPP_FETCH_URL = "/api/integrations/daowhatsapp";
const REQUEST_TIMEOUT_MS = 15000;

/** key -> resolved match (or null when the API returned none) */
const matchCache = new Map();
/** key -> in-flight promise, so concurrent mounts share one request */
const inFlight = new Map();

export const buildMatchKey = (productId, messageId) =>
  productId && messageId ? `${productId}|${messageId}` : null;

/** Test seam — Vitest has no page teardown between cases. */
export const __clearWhatsappMatchCache = () => {
  matchCache.clear();
  inFlight.clear();
};

const requestMatch = (productId, messageId, signal) => {
  const url =
    `${WHATSAPP_FETCH_URL}/matches/${productId}` +
    `?confidence_threshold=0.1&watch_count=all&limit=1&intent=Buy&message_id=${messageId}`;

  return axios
    .get(url, { timeout: REQUEST_TIMEOUT_MS, signal })
    .then((response) => response?.data?.matches?.[0] ?? null);
};

export default function useWhatsappMatch(productId, messageId) {
  const key = buildMatchKey(productId, messageId);

  // Seed synchronously from cache so a cached card paints without a
  // loading flash on remount.
  const [matchData, setMatchData] = useState(() =>
    key && matchCache.has(key) ? matchCache.get(key) : null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(() => {
    if (!key) return undefined;

    if (matchCache.has(key)) {
      setMatchData(matchCache.get(key));
      setIsLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    setIsLoading(true);
    setError(null);

    let promise = inFlight.get(key);
    if (!promise) {
      // The shared promise is deliberately NOT bound to this card's signal —
      // one card unmounting must not cancel the request others are awaiting.
      promise = requestMatch(productId, messageId)
        .then((match) => {
          matchCache.set(key, match);
          return match;
        })
        .finally(() => {
          inFlight.delete(key);
        });
      inFlight.set(key, promise);
    }

    promise
      .then((match) => {
        if (controller.signal.aborted || !mountedRef.current) return;
        setMatchData(match);
        setIsLoading(false);
      })
      .catch((err) => {
        if (controller.signal.aborted || !mountedRef.current) return;
        if (axios.isCancel?.(err) || err?.name === "CanceledError") return;
        setError(err);
        setIsLoading(false);
      });

    return () => controller.abort();
  }, [key, productId, messageId]);

  useEffect(() => load(), [load]);

  return { matchData, isLoading, error };
}
